import { PvpFlag } from "../zones/zones.ts";
import { mayHarm, pvpOpen, sharesParty, type Fighter } from "./may-harm.ts";

export { sharesParty };

/**
 * Who may help whom (ADR-079's amendment of 2026-10-03, item 2, and ADR-076's amendment of 2026-10-08, section D): the companion
 * of `mayHarm` for an ability aimed at an ally, over the same plain facts. The caller excludes self and the dead before
 * asking, as it does for `mayHarm`.
 */

/**
 * Whether one character may help another (ADR-076's amendment of 2026-10-08, section D1). Flagged means open to player
 * combat (`pvpOpen`): Combatant anywhere, or Flagged outside a Sanctuary; unflagged is the rest, a character sheltered in
 * a Sanctuary included. A held side closes every case, a creature neither helps nor is helped, and a duel is one against
 * one (Bible 7.1), so nobody helps a duellist and a duellist helps nobody, its opponent included. Then:
 * 1. an unflagged helper never helps a Flagged target, except a helper whose preference is Flag on helping a member of
 *    its own party (the author's answer 4), who becomes Combatant for it when the map server says so (D2);
 * 2. otherwise two members of one party (the same non-zero id) help each other;
 * 3. otherwise both must be unflagged.
 *
 * `mayHarm` is a guard: no pair that may harm each other helps. Members of one party are no foes outside a duel
 * (ADR-101 decision 27), so `mayHarm` already refuses them and the guard lets them through. The creature rule (D1, a fight with
 * creatures) and D9 (no help out of safety) need windows only the map server holds and are asked there.
 *
 * @param helper The side helping.
 * @param target The side helped.
 * @param truce Whether the map server's truce table holds the pair at this tick, passed to `mayHarm`; a truce stops harm and makes no ally.
 * @returns True when the help may land.
 * @example
 * mayHelp({ kind: "character", flag: 0, zone: 0, duel: 0, held: false, party: 0, preference: 0 }, { kind: "character", flag: 2, zone: 0, duel: 0, held: false, party: 0, preference: 0 }, false);
 * // false: a Protected character never helps a Combatant one
 */
export function mayHelp(helper: Fighter, target: Fighter, truce: boolean): boolean {
  if (helper.held || target.held || helper.kind !== "character" || target.kind !== "character" || helper.duel !== 0 || target.duel !== 0) {
    return false;
  }
  return helpDenial(helper, target) === HelpDenial.None && !mayHarm(helper, target, truce);
}

/**
 * Which of the rules refused a help between two characters outside a duel and a hold, so the map server can tell the
 * helper why (ADR-076's amendment of 2026-10-08, D4): `Flagged` for rule 1, which becomes the refusal 16, and `Party`
 * for rules 2 and 3, which becomes 17.
 */
export const HelpDenial = { None: 0, Flagged: 1, Party: 2 } as const;
export type HelpDenial = (typeof HelpDenial)[keyof typeof HelpDenial];

/**
 * The rule that refuses a help, or none: rule 1 first (an unflagged helper and a Flagged target, outside the carve-out
 * of a helper with Flag on helping a member of its party), then rules 2 and 3 (a Flagged helper, or any helper not
 * unflagged with an unflagged target, outside the helper's party). The caller has excluded creatures, duels and a held
 * side, as `mayHelp` does.
 *
 * @param helper The side helping.
 * @param target The side helped.
 * @returns A `HelpDenial`.
 */
export function helpDenial(helper: Fighter, target: Fighter): HelpDenial {
  const mates = sharesParty(helper, target);
  const open = pvpOpen(helper);
  if (!open && pvpOpen(target)) {
    return mates && helper.preference === PvpFlag.Flagged ? HelpDenial.None : HelpDenial.Flagged;
  }
  return mates || !open ? HelpDenial.None : HelpDenial.Party;
}
